import { QrCode, Copy, Download, Trash2, FileSpreadsheet, Printer, Ban, RotateCcw } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";

const BATCHES = [25, 50, 100, 300];

export default function AdminKitCodes({ ctx }) {
  const {
    tutorials, kitTutorialId, setKitTutorialId, kitCount, setKitCount,
    generateKitCodes, kitQrUrl, downloadQR, kitCodes, copyUnusedCodes,
    delKitCode, toggleKitCodeStatus,
  } = ctx;

  const kitTut = tutorials.find((t) => t.id === kitTutorialId);
  const kitName = kitTut?.title || "Cake Kit Tutorial";
  const unused = kitCodes.filter((c) => c.status === "unused");
  const redeemed = kitCodes.filter((c) => c.status === "redeemed");
  const disabled = kitCodes.filter((c) => c.status === "disabled");

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return iso; } };

  const downloadCSV = () => {
    if (kitCodes.length === 0) { toast.error("No codes to export"); return; }
    const rows = [["Code", "Status", "Redeemed by (email)", "Redeemed on", "Tutorial"]];
    kitCodes.forEach((c) => rows.push([
      c.code, c.status, c.redeemed_email || "", c.redeemed_at ? new Date(c.redeemed_at).toISOString() : "", c.tutorial_title || kitName,
    ]));
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `kit-codes-${(kitTut?.slug || kitTutorialId)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const printLabels = () => {
    if (unused.length === 0) { toast.error("No unused codes to print"); return; }
    const canvas = document.querySelector("#kit-qr-hires canvas");
    const qrData = canvas ? canvas.toDataURL("image/png") : "";
    const labels = unused.map((c) => `
      <div class="label">
        <div class="kit">${kitName}</div>
        ${qrData ? `<img class="qr" src="${qrData}" />` : ""}
        <div class="instr">Scan to unlock your tutorial</div>
        <div class="code">${c.code}</div>
      </div>`).join("");
    const html = `<!doctype html><html><head><title>Kit Labels — ${kitName}</title>
      <style>
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { margin: 0; font-family: Arial, Helvetica, sans-serif; }
        .sheet { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; padding: 16px; }
        .label { border: 2px solid #121212; border-radius: 8px; padding: 12px 8px; text-align: center;
                 display: flex; flex-direction: column; align-items: center; gap: 6px; break-inside: avoid; page-break-inside: avoid; }
        .kit { font-weight: 800; font-size: 13px; color: #121212; line-height: 1.15; }
        .qr { width: 120px; height: 120px; }
        .instr { font-size: 10px; letter-spacing: 0.5px; text-transform: uppercase; color: #444; }
        .code { font-family: "Courier New", monospace; font-weight: 700; font-size: 15px; letter-spacing: 1px;
                background: #C6FF33; padding: 4px 8px; border-radius: 4px; }
        @page { margin: 10mm; }
      </style></head>
      <body><div class="sheet">${labels}</div>
      <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups to print labels."); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  return (
    <div className="mt-10" data-testid="admin-kitcodes">
      <p className="text-sm text-stone-500 mb-5">Each kit design has <b>one permanent QR code</b> that opens its tutorial's redemption page. Print the QR + a unique code on each box insert — customers scan it, sign in, enter their code, and the tutorial is saved to their account forever.</p>
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="p-6 rounded-2xl bg-white border border-stone-200 space-y-4">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-1">Kit tutorial</label>
            <select data-testid="kit-tutorial-select" value={kitTutorialId} onChange={(e) => setKitTutorialId(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white focus:border-[#121212] focus:outline-none">
              <option value="">— select —</option>
              {tutorials.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-2">How many codes</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {BATCHES.map((n) => (
                <button key={n} data-testid={`kit-batch-${n}`} onClick={() => setKitCount(n)}
                  className={`px-4 py-2 text-xs tracking-[0.15em] uppercase font-bold border-2 transition-colors ${Number(kitCount) === n ? "bg-[#121212] text-[#F2E9D8] border-[#121212]" : "border-stone-300 text-stone-600 hover:border-[#121212]"}`}>{n}</button>
              ))}
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <input data-testid="kit-count-input" type="number" min="1" max="300" value={kitCount} onChange={(e) => setKitCount(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#121212] focus:outline-none" />
              </div>
              <button data-testid="generate-kit-codes-btn" onClick={generateKitCodes} className="px-6 py-2.5 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.2em] uppercase font-bold transition-all">Generate</button>
            </div>
            <p className="text-[11px] text-stone-400 mt-1">Up to 300 at a time. Run it again for larger runs.</p>
          </div>
          {kitQrUrl && (
            <div className="pt-4 border-t border-stone-200">
              <p className="text-xs tracking-[0.2em] uppercase text-stone-500 mb-3 inline-flex items-center gap-2"><QrCode size={14} /> Permanent box QR code</p>
              <div id="kit-qr-canvas" className="inline-block p-3 bg-white border-2 border-[#121212]">
                <QRCodeCanvas value={kitQrUrl} size={160} level="M" includeMargin={false} />
              </div>
              {/* hidden hi-res canvas for downloads & printed labels */}
              <div id="kit-qr-hires" style={{ position: "absolute", left: "-9999px", top: 0 }} aria-hidden="true">
                <QRCodeCanvas value={kitQrUrl} size={512} level="M" includeMargin={false} />
              </div>
              <p className="text-[11px] text-stone-400 mt-2 break-all">{kitQrUrl}</p>
              <button data-testid="download-qr-btn" onClick={downloadQR} className="mt-3 inline-flex items-center gap-2 px-4 py-2 border-2 border-[#121212] text-[#121212] hover:bg-[#121212] hover:text-[#F2E9D8] text-xs tracking-[0.2em] uppercase font-bold transition-colors"><Download size={14} /> Download QR (PNG)</button>
            </div>
          )}
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-medium text-stone-700">{kitCodes.length} codes · {unused.length} unused · {redeemed.length} redeemed{disabled.length ? ` · ${disabled.length} disabled` : ""}</p>
          </div>
          {kitCodes.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button data-testid="download-csv-btn" onClick={downloadCSV} className="inline-flex items-center gap-1.5 px-3 py-2 border border-stone-300 text-stone-600 hover:border-[#121212] hover:text-[#121212] text-xs tracking-[0.15em] uppercase font-bold rounded-lg transition-colors"><FileSpreadsheet size={14} /> CSV</button>
              <button data-testid="print-labels-btn" onClick={printLabels} className="inline-flex items-center gap-1.5 px-3 py-2 border border-stone-300 text-stone-600 hover:border-[#121212] hover:text-[#121212] text-xs tracking-[0.15em] uppercase font-bold rounded-lg transition-colors"><Printer size={14} /> Print labels</button>
              {unused.length > 0 && (
                <button data-testid="copy-codes-btn" onClick={copyUnusedCodes} className="inline-flex items-center gap-1.5 px-3 py-2 border border-stone-300 text-stone-600 hover:border-[#121212] hover:text-[#121212] text-xs tracking-[0.15em] uppercase font-bold rounded-lg transition-colors"><Copy size={13} /> Copy unused</button>
              )}
            </div>
          )}
          <div className="max-h-[520px] overflow-y-auto space-y-2 pr-1">
            {kitCodes.length === 0 ? <p className="text-stone-500 text-sm">No codes yet for this kit.</p> :
              kitCodes.map((c) => (
                <div key={c.id} data-testid={`kit-code-${c.id}`} className={`p-3 bg-white border rounded-lg flex items-center justify-between gap-3 ${c.status === "disabled" ? "border-stone-200 opacity-60" : "border-stone-200"}`}>
                  <div className="min-w-0">
                    <p className={`font-mono font-bold text-[#121212] ${c.status === "disabled" ? "line-through" : ""}`}>{c.code}</p>
                    <p className="text-xs text-stone-500">
                      {c.status === "redeemed"
                        ? `Redeemed by ${c.redeemed_email || "account"}${c.redeemed_at ? ` · ${fmtDate(c.redeemed_at)}` : ""}`
                        : c.status === "disabled" ? "Disabled — cannot be redeemed" : "Unused"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] tracking-[0.15em] uppercase font-bold px-2 py-1 ${c.status === "redeemed" ? "bg-emerald-100 text-emerald-800" : c.status === "disabled" ? "bg-stone-300 text-stone-700" : "bg-[#C6FF33] text-[#121212]"}`}>{c.status}</span>
                    {c.status !== "redeemed" && (
                      <button data-testid={`toggle-kit-code-${c.id}`} title={c.status === "disabled" ? "Re-enable code" : "Disable code"} onClick={() => toggleKitCodeStatus(c)} className="p-1.5 text-stone-400 hover:text-[#121212]">
                        {c.status === "disabled" ? <RotateCcw size={15} /> : <Ban size={15} />}
                      </button>
                    )}
                    <button data-testid={`del-kit-code-${c.id}`} onClick={() => delKitCode(c.id)} className="p-1.5 text-stone-400 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
